import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiChevronRight } from "react-icons/fi";
import { useColorModeValue } from "../components/ui/color-mode";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useRecentStocks } from "../hooks/useRecentStocks";
import { useStockInfo } from "../hooks/useStockInfo";
import StockLogo from "../components/StockLogo";
import StockSearch from "../components/StockSearch";
import { browseStocks, getStockClassifications } from "../api";

const DIRECTORY_PAGE_SIZE = 24;

const formatMarketCap = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(numeric);
};

const activateCompanyRow = (event, navigate, ticker) => {
  if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;
  if (event.type === "keydown") event.preventDefault();
  navigate(`/stock/${ticker.toLowerCase()}`);
};

const RecentStockItem = ({ symbol, lastVisitedAt }) => {
  const { data: stockInfo, loading } = useStockInfo(symbol);
  const companyName = stockInfo?.company_name || symbol;
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box
      as="button"
      onClick={() => navigate(`/stock/${symbol.toLowerCase()}`)}
      bg="rgba(255, 255, 255, 0.5)"
      _dark={{ bg: "rgba(0, 0, 0, 0.3)" }}
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      textAlign="center"
      w="100%"
      maxW="300px"
      _hover={{
        bg: "rgba(255, 255, 255, 0.7)",
        _dark: { bg: "rgba(0, 0, 0, 0.5)" },
        transform: "translateY(-2px)",
        boxShadow: "md",
      }}
      transition="all 0.2s"
      cursor="pointer"
    >
      <VStack gap={2}>
        <StockLogo ticker={symbol} companyName={companyName} />
        <Text fontSize="lg" fontWeight="bold">
          {symbol}
        </Text>
        <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
          {loading ? "Loading..." : companyName}
        </Text>
        <Text fontSize="xs" color="gray.500" _dark={{ color: "gray.400" }}>
          Last opened: {formatDate(lastVisitedAt)}
        </Text>
      </VStack>
    </Box>
  );
};

const Browse = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth0();
  const { recents, loading: recentsLoading, error: recentsError, addRecent } = useRecentStocks();
  const sector = searchParams.get("sector") || "";
  const industry = searchParams.get("industry") || "";
  const [directoryQuery, setDirectoryQuery] = useState("");
  const [debouncedDirectoryQuery, setDebouncedDirectoryQuery] = useState("");
  const [listingStatus, setListingStatus] = useState("all");
  const [sort, setSort] = useState("market_cap");
  const [offset, setOffset] = useState(0);
  const [classifications, setClassifications] = useState({ sectors: [], industries: [] });
  const [directory, setDirectory] = useState({ results: [], total: 0 });
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [directoryError, setDirectoryError] = useState("");
  const [classificationError, setClassificationError] = useState("");

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedDirectoryQuery(directoryQuery), 250);
    return () => clearTimeout(timeoutId);
  }, [directoryQuery]);

  useEffect(() => {
    setOffset(0);
  }, [industry, sector]);

  useEffect(() => {
    const controller = new AbortController();
    setClassificationError("");
    getStockClassifications({ sector, signal: controller.signal })
      .then((data) => setClassifications({
        sectors: data?.sectors || [],
        industries: data?.industries || [],
      }))
      .catch((error) => {
        if (error.name !== "AbortError") {
          setClassificationError(error.message || "Unable to load stock classifications.");
        }
      });
    return () => controller.abort();
  }, [sector]);

  useEffect(() => {
    const controller = new AbortController();
    setDirectoryLoading(true);
    setDirectoryError("");
    browseStocks({
      query: debouncedDirectoryQuery,
      sector,
      industry,
      sort,
      listingStatus,
      limit: DIRECTORY_PAGE_SIZE,
      offset,
      signal: controller.signal,
    })
      .then((data) => setDirectory({
        results: data?.results || [],
        total: data?.total || 0,
      }))
      .catch((error) => {
        if (error.name !== "AbortError") {
          setDirectoryError(error.message || "Unable to browse stocks.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDirectoryLoading(false);
      });
    return () => controller.abort();
  }, [debouncedDirectoryQuery, industry, listingStatus, offset, sector, sort]);

  const handleSuggestionSelected = async (suggestion) => {
    if (suggestion?.entity_type === "pre_ipo" || !suggestion?.ticker) return;
    try {
      await addRecent?.(suggestion.ticker);
    } catch (err) {
      console.error("Failed to record recent stock", err);
    }
  };

  const bgStart = useColorModeValue("gray.50", "gray.800");
  const bgEnd = useColorModeValue("teal.200", "teal.900");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const searchBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.800");

  const updateSector = (event) => {
    const nextSector = event.target.value;
    setOffset(0);
    setSearchParams(nextSector ? { sector: nextSector } : {});
  };

  const updateIndustry = (event) => {
    const nextIndustry = event.target.value;
    setOffset(0);
    setSearchParams(nextIndustry ? { industry: nextIndustry } : sector ? { sector } : {});
  };

  const updateDirectoryQuery = (event) => {
    setDirectoryQuery(event.target.value);
    setOffset(0);
  };

  const pageStart = directory.total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + DIRECTORY_PAGE_SIZE, directory.total);

  return (
    <Flex
      minH="calc(100vh - var(--nav-h))"
      direction="column"
      justify="flex-start"
      bgGradient="to-b"
      gradientFrom={bgStart}
      gradientTo={bgEnd}
      color={textColor}
      px={{ base: 5, md: 8 }}
      py={{ base: 12, md: 16 }}
    >
      <VStack gap={6} w="100%" maxW="960px" mx="auto" align="stretch">
        <Box textAlign="center">
          <Text as="h1" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
            Browse Stocks
          </Text>
          <Text mt={2} color={mutedTextColor} fontSize={{ base: "md", md: "lg" }}>
            Search public stocks by ticker or company name, or find pre-IPO SEC filers by
            legal name, CIK, or proposed ticker.
          </Text>
        </Box>

        <Box
          bg={searchBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="md"
          p={{ base: 4, md: 5 }}
          position="relative"
        >
          <StockSearch
            onSuggestionSelected={handleSuggestionSelected}
            inputProps={{
              "aria-label": "Search by ticker, company name, or CIK",
            }}
          />
        </Box>

        <Box
          bg={searchBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="md"
          p={{ base: 4, md: 6 }}
        >
          <Heading as="h2" size="lg">Explore by sector and industry</Heading>
          <Text mt={2} color={mutedTextColor}>
            Filter listed companies using Nasdaq classifications, with clearly labeled SEC SIC
            classifications where Nasdaq metadata is unavailable.
          </Text>
          {(sector || industry) && (
            <Box mt={4} px={4} py={3} borderRadius="md" bg="teal.50" _dark={{ bg: "teal.950" }}>
              <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="teal.700" _dark={{ color: "teal.200" }}>
                Showing companies by {industry ? "industry" : "sector"}
              </Text>
              <Text fontWeight="semibold" mt={1}>{industry || sector}</Text>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} gap={3} mt={5}>
            <Box>
              <Text as="label" htmlFor="directory-company-filter" fontSize="sm" fontWeight="semibold">
                Company
              </Text>
              <Input
                id="directory-company-filter"
                value={directoryQuery}
                onChange={updateDirectoryQuery}
                placeholder="Filter by name or ticker"
                mt={1}
              />
            </Box>
            <Box>
              <Text as="label" htmlFor="directory-sector-filter" fontSize="sm" fontWeight="semibold">
                Sector
              </Text>
              <NativeSelect.Root mt={1}>
                <NativeSelect.Field id="directory-sector-filter" value={sector} onChange={updateSector}>
                  <option value="">All sectors</option>
                  {classifications.sectors.map((option) => (
                    <option key={option.sector} value={option.sector}>
                      {option.sector} ({option.count})
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
            <Box>
              <Text as="label" htmlFor="directory-industry-filter" fontSize="sm" fontWeight="semibold">
                Industry
              </Text>
              <NativeSelect.Root mt={1}>
                <NativeSelect.Field id="directory-industry-filter" value={industry} onChange={updateIndustry}>
                  <option value="">All industries</option>
                  {classifications.industries.map((option) => (
                    <option key={option.industry} value={option.industry}>
                      {option.industry} ({option.count})
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
            <Box>
              <Text as="label" htmlFor="directory-listing-status" fontSize="sm" fontWeight="semibold">
                Listing status
              </Text>
              <NativeSelect.Root mt={1}>
                <NativeSelect.Field id="directory-listing-status" value={listingStatus} onChange={(event) => {
                  setListingStatus(event.target.value);
                  setOffset(0);
                }}>
                  <option value="all">All companies</option>
                  <option value="listed">Listed companies</option>
                  <option value="archived">Formerly public</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
            <Box>
              <Text as="label" htmlFor="directory-sort" fontSize="sm" fontWeight="semibold">
                Sort by
              </Text>
              <NativeSelect.Root mt={1}>
                <NativeSelect.Field
                  id="directory-sort"
                  value={sort}
                  onChange={(event) => {
                    setSort(event.target.value);
                    setOffset(0);
                  }}
                >
                  <option value="market_cap">Market cap</option>
                  <option value="name">Company name</option>
                  <option value="ticker">Ticker</option>
                  <option value="sector">Sector</option>
                  <option value="industry">Industry</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
          </SimpleGrid>

          {classificationError && <Text color="red.500" mt={5}>{classificationError}</Text>}
          {directoryError && <Text color="red.500" mt={5}>{directoryError}</Text>}
          {directoryLoading && (
            <Flex justify="center" py={10}><Spinner aria-label="Loading stock directory" /></Flex>
          )}
          {!directoryLoading && !directoryError && directory.results.length === 0 && (
            <Text color={mutedTextColor} py={10} textAlign="center">
              No companies match these filters.
            </Text>
          )}
          {!directoryLoading && !directoryError && directory.results.length > 0 && (
            <>
              <Flex justify="space-between" align="center" mt={5} mb={3} gap={3} wrap="wrap">
                <Text fontSize="sm" color={mutedTextColor}>
                  Showing {pageStart}–{pageEnd} of {directory.total.toLocaleString()} companies
                </Text>
              </Flex>
              <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
                <Table.Root size="sm" tableLayout="fixed">
                  <Table.Header bg={tableHeaderBg}>
                    <Table.Row>
                      <Table.ColumnHeader w={{ base: "72px", md: "90px" }}>Ticker</Table.ColumnHeader>
                      <Table.ColumnHeader>Company</Table.ColumnHeader>
                      {!sector && (
                        <Table.ColumnHeader w="150px" display={{ base: "none", lg: "table-cell" }}>
                          Sector
                        </Table.ColumnHeader>
                      )}
                      <Table.ColumnHeader w="300px" display={{ base: "none", md: "table-cell" }}>
                        Industry
                      </Table.ColumnHeader>
                      <Table.ColumnHeader w={{ base: "94px", md: "120px" }} textAlign="right">
                        Market cap
                      </Table.ColumnHeader>
                      <Table.ColumnHeader w="34px" aria-label="Open company" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {directory.results.map((company) => {
                      const marketCap = formatMarketCap(company.market_cap);
                      return (
                        <Table.Row
                          key={company.ticker}
                          tabIndex={0}
                          aria-label={`Open ${company.ticker} ${company.name}`}
                          cursor="pointer"
                          onClick={(event) => activateCompanyRow(event, navigate, company.ticker)}
                          onKeyDown={(event) => activateCompanyRow(event, navigate, company.ticker)}
                          _hover={{ bg: "teal.50", _dark: { bg: "teal.950" } }}
                          _focusVisible={{ outline: "2px solid", outlineColor: "teal.500", outlineOffset: "-2px" }}
                        >
                          <Table.Cell py={3} fontWeight="bold" color="teal.700" _dark={{ color: "teal.200" }}>
                            <Flex align="center" gap={2}>
                              <StockLogo ticker={company.ticker} companyName={company.name} size={28} />
                              {company.ticker}
                            </Flex>
                          </Table.Cell>
                          <Table.Cell py={3} minW={0}>
                            <Text truncate fontWeight="medium" title={company.name}>{company.name}</Text>
                            {company.listing_status === "archived" && <Badge mt={1} colorPalette="gray">Formerly public</Badge>}
                          </Table.Cell>
                          {!sector && (
                            <Table.Cell py={3} display={{ base: "none", lg: "table-cell" }}>
                              <Text truncate color={mutedTextColor} title={company.sector}>{company.sector || "—"}</Text>
                            </Table.Cell>
                          )}
                          <Table.Cell py={3} display={{ base: "none", md: "table-cell" }}>
                            <Flex align="center" gap={2} minW={0}>
                              <Text truncate color={mutedTextColor} title={company.industry}>{company.industry || "—"}</Text>
                              {company.classification_source === "sec_sic" && (
                                <Badge flex="0 0 auto" size="xs" colorPalette="orange" variant="subtle">
                                  SEC SIC{company.sec_sic ? ` ${company.sec_sic}` : ""}
                                </Badge>
                              )}
                            </Flex>
                          </Table.Cell>
                          <Table.Cell py={3} textAlign="right" fontWeight="semibold" whiteSpace="nowrap">
                            {marketCap || "—"}
                          </Table.Cell>
                          <Table.Cell py={3} px={1} color={mutedTextColor}>
                            <FiChevronRight aria-hidden="true" />
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              </Box>
              <Flex justify="space-between" mt={5}>
                <Button
                  variant="outline"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - DIRECTORY_PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  disabled={pageEnd >= directory.total}
                  onClick={() => setOffset(offset + DIRECTORY_PAGE_SIZE)}
                >
                  Next
                </Button>
              </Flex>
            </>
          )}
        </Box>
      </VStack>

      {isAuthenticated && (
        <Box
          bg="rgba(255, 255, 255, 0.5)"
          _dark={{ bg: "rgba(0, 0, 0, 0.3)" }}
          borderTopWidth="1px"
          py={6}
          px={6}
          mt={8}
          w="100%"
        >
          <VStack gap={6} maxW="1200px" mx="auto" align="center">
            <Text fontSize="xl" fontWeight="semibold" textAlign="center">
              Recently visited
            </Text>
            {recentsLoading && <Spinner size="sm" />}
            {recentsError && (
              <Text fontSize="sm" color="red.500" textAlign="center">
                {recentsError}
              </Text>
            )}
            {!recentsLoading && !recentsError && recents.length === 0 && (
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Your recent stocks will appear here after you browse.
              </Text>
            )}
            {!recentsLoading && recents.length > 0 && (
              <Flex wrap="wrap" gap={4} justify="center">
                {recents.map((r) => (
                  <RecentStockItem
                    key={r.symbol}
                    symbol={r.symbol}
                    lastVisitedAt={r.last_visited_at}
                  />
                ))}
              </Flex>
            )}
          </VStack>
        </Box>
      )}
    </Flex>
  );
};

export default Browse;
